import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import alert from '@jetbrains/ring-ui/components/alert-service/alert-service';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod/dist/zod';
import { Content } from '@jetbrains/ring-ui/components/island/island';
import Panel from '@jetbrains/ring-ui/components/panel/panel';
import Button from '@jetbrains/ring-ui/components/button/button';
import { Navigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { useDialogCancelConfirmation } from '@app/common/hooks/use-dialog-cancel-confirmation';
import { Breadcrumbs } from '@app/common/page-layout/breadcrumbs';
import { ALL_KINDS } from '@modules/ide-provisioner/ide-settings/settings-builders';
import { ToolsFilter } from '@modules/applications/tools-filter';
import { Dialog } from '@common/dialog/dialog';
import { getApplications } from '@common/store/profiles/selectors';
import { useEvaluatedSettingFilters } from '@common/queries/profile-evaluations';
import DialogHeader from '../../common/dialog-header';
import { getIdeSettingsFormValues } from '../../schemas/ctors';
import { tIdeSettingsFormValuesSchema } from '../../schemas/ide-settings-form-values-schema';
import JsonEditorFields from './json-editor/json-editor-fields';
import SettingFields from './form/setting-fields';
import AddSettingsFields from './form/add-settings-fields';
import { tryParseIdeSettingArray } from './json-editor/json-utils';
import styles from './profiles-ide-settings-dialog-form.css';
function isOnPasteDisabled(e, selectedSettingIndex) {
    if (selectedSettingIndex !== undefined) {
        return true;
    }
    // @ts-ignore
    const tagName = e.target.tagName.toLowerCase();
    return tagName === 'textarea' || tagName === 'input';
}
function hasOnlyJsonEditor(value) {
    return value.kind === 'KEY_VALUE_LIST' || !ALL_KINDS.some(it => it === value.kind);
}
function ProfilesIdeSettingsDialogForm({ onReject, onSubmit, initialFormValues }) {
    var _a, _b, _c, _d;
    const { id: profileId } = useParams();
    const [showJsonEditor, setShowJsonEditor] = useState(false);
    const form = useForm({
        defaultValues: initialFormValues !== null && initialFormValues !== void 0 ? initialFormValues : getIdeSettingsFormValues(),
        resolver: zodResolver(tIdeSettingsFormValuesSchema)
    });
    const { formState: { isDirty, isSubmitting, errors, isValid }, handleSubmit, setValue, watch } = form;
    const setFilters = (filters) => {
        setValue('filters', filters, {
            shouldDirty: true,
            shouldValidate: true
        });
    };
    const watchFilters = watch('filters');
    const applications = useSelector(getApplications);
    const { isFetching: isFilterToolsLoading, data: filterTools } = useEvaluatedSettingFilters(applications.rules, watchFilters);
    const [selectedSettingIndex, setSelectedSettingIndex] = useState();
    const editorDefaultValue = typeof selectedSettingIndex === 'number'
        ? form.getValues(`content.${selectedSettingIndex}`)
        : undefined;
    const isOnlyJsonEditorAvailable = useMemo(() => (editorDefaultValue ? hasOnlyJsonEditor(editorDefaultValue) : false), [editorDefaultValue]);
    useEffect(() => {
        setShowJsonEditor(isOnlyJsonEditorAvailable);
    }, [isOnlyJsonEditorAvailable]);
    const titleParts = useMemo(() => [
        { title: initialFormValues ? 'Edit IDE Settings' : 'Add IDE Settings' },
        ...(selectedSettingIndex === undefined ? [] : [{ title: 'Configure' }])
    ], [selectedSettingIndex, initialFormValues]);
    const canSubmit = isDirty && isValid && !isFilterToolsLoading;
    const onValid = (formValues) => {
        onSubmit({ ...formValues, tools: filterTools !== null && filterTools !== void 0 ? filterTools : [] });
    };
    const { onCloseAttempt, cancelButtonRef } = useDialogCancelConfirmation(onReject, isDirty);
    const handlePaste = (event) => {
        if (isOnPasteDisabled(event, selectedSettingIndex)) {
            return;
        }
        const clipboardData = event.clipboardData.getData('text');
        if (clipboardData.length > 0) {
            const newItems = tryParseIdeSettingArray(clipboardData);
            if (newItems.length > 0) {
                const currentContent = form.getValues('content');
                const newContent = currentContent.concat(newItems);
                form.setValue('content', newContent, { shouldDirty: true });
            }
            else {
                alert.error(`Failed to parse settings`);
            }
        }
    };
    if (!profileId) {
        return _jsx(Navigate, { to: "/profiles" });
    }
    return (_jsx(FormProvider, { ...form, children: _jsxs(Dialog, { autoFocusFirst: false, show: true, onCloseAttempt: onCloseAttempt, onPaste: handlePaste, size: "wide", children: [_jsxs(DialogHeader, { children: [_jsx(Breadcrumbs, { breadcrumbs: titleParts }), selectedSettingIndex !== undefined && showJsonEditor && !isOnlyJsonEditorAvailable && (_jsx(Button, { text: true, className: classNames(styles.formEditorButton, styles.dialogHeaderButton), onClick: () => setShowJsonEditor(false), children: "Back to form" })), selectedSettingIndex !== undefined && !showJsonEditor && (_jsx(Button, { text: true, className: classNames(styles.jsonEditorButton, styles.dialogHeaderButton), onClick: () => setShowJsonEditor(true), children: "Open JSON editor" }))] }), _jsx(Content, { children: _jsxs("form", { onSubmit: handleSubmit(onValid), children: [selectedSettingIndex === undefined && (_jsxs(_Fragment, { children: [_jsx(ToolsFilter, { filters: watchFilters, onFiltersChange: setFilters, target: "settings", profileId: profileId, isFilterToolsLoading: isFilterToolsLoading, filterTools: filterTools !== null && filterTools !== void 0 ? filterTools : [], inheritedFilters: applications.rules }), _jsx(AddSettingsFields, { className: styles.addSettingsFields, onSettingSelect: setSelectedSettingIndex }), _jsx("input", { type: "submit", hidden: true, disabled: !canSubmit })] })), selectedSettingIndex !== undefined && !showJsonEditor && (_jsx(SettingFields, { settingIndex: selectedSettingIndex })), selectedSettingIndex !== undefined && showJsonEditor && (_jsx(JsonEditorFields, { settingIndex: selectedSettingIndex })), (((_a = errors.content) === null || _a === void 0 ? void 0 : _a.message) || ((_b = errors.filters) === null || _b === void 0 ? void 0 : _b.message)) && (_jsx("div", { className: styles.errorMessageContainer, children: _jsxs("div", { className: styles.contentErrorMessage, children: [((_c = errors.content) === null || _c === void 0 ? void 0 : _c.message) && _jsx("p", { children: errors.content.message }), ((_d = errors.filters) === null || _d === void 0 ? void 0 : _d.message) && _jsx("p", { children: errors.filters.message })] }) }))] }) }), _jsx(Panel, { children: selectedSettingIndex === undefined ? (_jsxs(_Fragment, { children: [_jsx(Button, { primary: true, loader: isSubmitting, disabled: !canSubmit, onClick: handleSubmit(onValid), children: "Save" }), _jsx(Button, { ref: cancelButtonRef, onClick: onReject, children: "Cancel" })] })) : (_jsx(Button, { onClick: () => setSelectedSettingIndex(undefined), ref: cancelButtonRef, children: "\u2190 Back" })) })] }) }));
}
export default ProfilesIdeSettingsDialogForm;
